import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Button from '../../../../components/ui/Button';
import { API_ENDPOINTS } from '../../../../utils/config';
import DocumentModalShell from './DocumentModalShell';
import {
  numberToWords,
  parseCompanyProfile,
  getBillToFromInvoice,
  getInvoiceLineItems,
} from '../utils/documentModalHelpers';

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const InvoiceDetailsModal = ({ isOpen, onClose, invoice, documentKind = 'invoice' }) => {
  const isEstimate = documentKind === 'estimate';
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [resolvedCustomer, setResolvedCustomer] = useState(null);
  const [resolvedClient, setResolvedClient] = useState(null);

  const lineItems = useMemo(() => getInvoiceLineItems(invoice), [invoice]);

  const billTo = useMemo(
    () => getBillToFromInvoice(invoice, resolvedCustomer, resolvedClient),
    [invoice, resolvedCustomer, resolvedClient]
  );

  useEffect(() => {
    if (!isOpen || !invoice) {
      setResolvedCustomer(null);
      setResolvedClient(null);
      return;
    }

    let cancelled = false;
    const headers = getAuthHeaders();

    const fetchContacts = async () => {
      let cust = null;
      let cli = null;

      const rawCustomer = invoice.customer;
      const rawClient = invoice.client;

      const customerId =
        rawCustomer != null && typeof rawCustomer !== 'object'
          ? rawCustomer
          : null;
      const clientId =
        rawClient != null && typeof rawClient !== 'object' ? rawClient : null;

      if (customerId != null && customerId !== '') {
        try {
          const r = await axios.get(API_ENDPOINTS.CUSTOMERS_DETAIL(customerId), { headers });
          if (!cancelled) cust = r.data;
        } catch {
          /* keep flat fields from invoice */
        }
      }

      if (clientId != null && clientId !== '') {
        try {
          const r = await axios.get(API_ENDPOINTS.CLIENTS_DETAIL(clientId), { headers });
          if (!cancelled) cli = r.data;
        } catch {
          /* keep flat fields */
        }
      }

      if (!cancelled) {
        setResolvedCustomer(cust);
        setResolvedClient(cli);
      }
    };

    fetchContacts();
    return () => {
      cancelled = true;
    };
  }, [isOpen, invoice?.id, invoice?.customer, invoice?.client]);

  useEffect(() => {
    if (isOpen && invoice?.id && !isEstimate) {
      fetchPaymentStatus();
    } else {
      setPaymentStatus(null);
    }
  }, [isOpen, invoice, isEstimate]);

  useEffect(() => {
    if (isOpen) {
      const headers = getAuthHeaders();
      axios
        .get(API_ENDPOINTS.COMPANY_PROFILE_LIST, { headers })
        .then((res) => setCompanyProfile(parseCompanyProfile(res.data)))
        .catch(() => setCompanyProfile(null));
    }
  }, [isOpen]);

  const fetchPaymentStatus = async () => {
    if (!invoice?.id) return;
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(API_ENDPOINTS.INVOICES_PAYMENT_STATUS(invoice.id), { headers });
      setPaymentStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch payment status:', error);
    }
  };

  if (!isOpen || !invoice) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'sent':
        return 'bg-blue-100 text-blue-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'overdue':
        return 'bg-red-100 text-red-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const numberLabel = isEstimate ? 'Estimate No' : 'Invoice No';
  const dateLabel = isEstimate ? 'Estimate Date' : 'Invoice Date';

  return (
    <DocumentModalShell
      title={isEstimate ? 'Estimate Details' : 'Invoice Details'}
      onClose={onClose}
      banner={isEstimate ? 'ESTIMATE' : 'TAX INVOICE'}
      companyProfile={companyProfile}
    >
      {/* Match TaxInvoiceForm: document column | Bill To column */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <p className="text-sm font-medium text-gray-700 mb-1">{numberLabel}</p>
            <p className="font-semibold">{invoice.invoice_number || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-700 mb-1">{dateLabel}</p>
            <p className="font-semibold">
              {invoice.invoice_date ? new Date(invoice.invoice_date).toLocaleDateString() : 'N/A'}
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-700 mb-1">Due Date</p>
            <p className="font-semibold">
              {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : 'N/A'}
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-700 mb-1">Status</p>
            <span
              className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(invoice.status)}`}
            >
              {invoice.status
                ? invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)
                : 'N/A'}
            </span>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-700 mb-1">Type</p>
            <p className="font-semibold capitalize">{invoice.type || 'N/A'}</p>
          </div>
        </div>
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-foreground">Bill To</h3>
          <div>
            <p className="text-sm font-medium text-gray-700 mb-1">Name</p>
            <p className="font-semibold">{billTo.name || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-700 mb-1">Company</p>
            <p className="font-semibold">{billTo.company || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-700 mb-1">Address</p>
            <p className="font-semibold">{billTo.address || 'N/A'}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-700 mb-1">GSTIN</p>
              <p className="font-semibold">{billTo.gstin || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700 mb-1">PAN</p>
              <p className="font-semibold">{billTo.pan || 'N/A'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Services — same columns as TaxInvoiceForm; document-level tax in summary below */}
      <div className="mt-6">
        <h3 className="text-base sm:text-lg font-semibold text-foreground mb-3 sm:mb-4">Services</h3>
        <div
          className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0 tax-invoice-table-scroll"
          style={{ scrollbarWidth: 'thin', scrollbarColor: '#cbd5e1 #f1f5f9' }}
        >
          <div className="inline-block min-w-full align-middle">
            <table className="min-w-[640px] sm:min-w-full w-full border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-50">
                  <th className="border border-gray-300 px-2 sm:px-4 py-2 text-left text-xs sm:text-sm font-medium whitespace-nowrap">
                    S.No
                  </th>
                  <th className="border border-gray-300 px-2 sm:px-4 py-2 text-left text-xs sm:text-sm font-medium whitespace-nowrap">
                    SAC / HSN Code
                  </th>
                  <th className="border border-gray-300 px-2 sm:px-4 py-2 text-left text-xs sm:text-sm font-medium min-w-[150px]">
                    Description of Goods / Services
                  </th>
                  <th className="border border-gray-300 px-2 sm:px-4 py-2 text-left text-xs sm:text-sm font-medium whitespace-nowrap">
                    Quantity
                  </th>
                  <th className="border border-gray-300 px-2 sm:px-4 py-2 text-left text-xs sm:text-sm font-medium whitespace-nowrap">
                    Amount (₹)
                  </th>
                </tr>
              </thead>
              <tbody>
                {lineItems.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="border border-gray-300 px-4 py-6 text-center text-sm text-muted-foreground"
                    >
                      No line items
                    </td>
                  </tr>
                ) : (
                  lineItems.map((item, idx) => {
                    const isService =
                      item.item_type === 'service' || (!!item.sac_code && !item.hsn_code);
                    const qty = isService ? 1 : parseFloat(item.quantity) || 1;
                    const rate =
                      item.rate != null
                        ? parseFloat(item.rate)
                        : qty
                          ? parseFloat(item.amount || 0) / qty
                          : parseFloat(item.amount || 0);
                    const lineAmt = parseFloat(item.amount) || rate * qty;
                    return (
                      <tr key={item.id ?? idx}>
                        <td className="border border-gray-300 px-2 sm:px-4 py-2 text-xs sm:text-sm">
                          {idx + 1}
                        </td>
                        <td className="border border-gray-300 px-2 sm:px-4 py-2 text-xs sm:text-sm">
                          {item.sac_code || item.hsn_code || '—'}
                        </td>
                        <td className="border border-gray-300 px-2 sm:px-4 py-2 text-xs sm:text-sm">
                          {item.description || '—'}
                        </td>
                        <td className="border border-gray-300 px-2 sm:px-4 py-2 text-xs sm:text-sm">
                          {isService ? '—' : qty}
                        </td>
                        <td className="border border-gray-300 px-2 sm:px-4 py-2 text-xs sm:text-sm">
                          ₹
                          {lineAmt.toLocaleString('en-IN', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Amount summary + bank — match TaxInvoiceForm lower grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
        <div className="bg-gray-50 p-3 sm:p-4 rounded-lg border border-gray-200">
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-gray-700">Total Taxable Value:</span>
              <span className="font-semibold">
                ₹
                {parseFloat(invoice.subtotal || 0).toLocaleString('en-IN', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-700">Tax Amount:</span>
              <span className="font-semibold">
                ₹
                {parseFloat(invoice.tax_amount || 0).toLocaleString('en-IN', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
            {parseFloat(invoice.adjustment_amount || 0) > 0 && (
              <div className="flex justify-between">
                <span className="text-sm text-gray-700">
                  {invoice.adjustment_type === 'discount' ? 'Discount' : 'Surcharge'}:
                </span>
                <span className="font-semibold">
                  ₹
                  {parseFloat(invoice.adjustment_amount || 0).toLocaleString('en-IN', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
            )}
            <div className="flex justify-between pt-2 border-t border-gray-300">
              <span className="font-semibold">
                {isEstimate ? 'Total Estimate Value:' : 'Total Invoice Value:'}
              </span>
              <span className="font-semibold">
                ₹
                {parseFloat(invoice.total_amount || 0).toLocaleString('en-IN', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
            <p className="text-xs text-gray-600 pt-2">
              Total (in words): {numberToWords(Math.floor(parseFloat(invoice.total_amount || 0)))} Rupees Only
            </p>
          </div>
        </div>
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Bank Details</h3>
          <div className="space-y-2 text-sm">
            <div>
              <span className="text-gray-600">Account Name:</span>{' '}
              <span>{companyProfile?.accountName || '—'}</span>
            </div>
            <div>
              <span className="text-gray-600">Bank Name:</span> <span>{companyProfile?.bankName || '—'}</span>
            </div>
            <div>
              <span className="text-gray-600">Account No:</span> <span>{companyProfile?.accountNo || '—'}</span>
            </div>
            <div>
              <span className="text-gray-600">IFSC:</span> <span>{companyProfile?.ifscCode || '—'}</span>
            </div>
          </div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2 mt-4">Signature</h3>
          <p className="text-sm text-gray-600">For {companyProfile?.name || 'COMPANY NAME'}</p>
          <div className="mt-8 border-t border-gray-300 pt-2">
            <p className="text-sm">Authorized Signatory</p>
          </div>
        </div>
      </div>

      {paymentStatus && !isEstimate && (
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mt-6">
          <h3 className="text-base font-semibold text-foreground mb-3">Payment Status</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-500">Fully Paid</p>
              <p className="font-semibold text-green-600">
                ₹
                {parseFloat(paymentStatus.fully_paid || 0).toLocaleString('en-IN', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Partial Paid</p>
              <p className="font-semibold text-yellow-600">
                ₹
                {parseFloat(paymentStatus.partial_paid || 0).toLocaleString('en-IN', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Receivable</p>
              <p className="font-semibold text-red-600">
                ₹
                {parseFloat(paymentStatus.receivable || 0).toLocaleString('en-IN', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Overpaid</p>
              <p className="font-semibold text-blue-600">
                ₹
                {parseFloat(paymentStatus.overpaid || 0).toLocaleString('en-IN', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </p>
            </div>
          </div>
        </div>
      )}

      {(invoice.notes || invoice.terms) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          {invoice.notes && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
              <p className="text-sm text-gray-600 border border-gray-200 rounded-md p-3 bg-gray-50">
                {invoice.notes}
              </p>
            </div>
          )}
          {invoice.terms && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Terms</label>
              <p className="text-sm text-gray-600 border border-gray-200 rounded-md p-3 bg-gray-50">
                {invoice.terms}
              </p>
            </div>
          )}
        </div>
      )}

      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mt-6">
        <h3 className="text-sm font-semibold text-foreground mb-3">Additional</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-gray-500">Template:</span>{' '}
            <span className="font-medium capitalize">{invoice.template_type || 'N/A'}</span>
          </div>
          <div>
            <span className="text-gray-500">E-Invoice:</span>{' '}
            <span className="font-medium capitalize">{invoice.einvoice_status || 'N/A'}</span>
          </div>
          {invoice.irn && (
            <div>
              <span className="text-gray-500">IRN:</span> <span className="font-medium">{invoice.irn}</span>
            </div>
          )}
          {invoice.created_at && (
            <div>
              <span className="text-gray-500">Created:</span>{' '}
              <span className="font-medium">{new Date(invoice.created_at).toLocaleString()}</span>
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end pt-4 border-t border-gray-200 mt-6">
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
      </div>
    </DocumentModalShell>
  );
};

export default InvoiceDetailsModal;
